import type { ReactNode } from 'react'
import { useState } from 'react'
import { CheckIcon, ChevronUpIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface OnboardingChecklistItem {
	id:    string
	label: string
	done:  boolean
}

export interface OnboardingChecklistProps {
	items:           OnboardingChecklistItem[]
	title?:          ReactNode
	completedLabel?: string
	className?:      string
}

export function OnboardingChecklist({ items, title, completedLabel = '% completed', className }: OnboardingChecklistProps) {
	const [open, setOpen] = useState(false)
	const total = items.length
	const doneCount = items.filter((i) => i.done).length
	const percent = total > 0 ? Math.round((doneCount / total) * 100) : 0

	return (
		<div className={cn('relative inline-block', className)}>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className={cn(
					'flex items-center gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-background) px-3 py-2 text-sm font-[500] text-(--color-foreground) shadow-(--shadow-sm) transition-colors hover:bg-(--color-muted)/50',
				)}
				aria-expanded={open}
			>
				<span>{title}</span>
				<span className="text-(--color-muted-foreground)">{percent}%</span>
				<ChevronUpIcon className={cn('size-4 transition-transform', open ? '' : 'rotate-180')} />
			</button>

			{open && (
				<div className="absolute bottom-full left-0 mb-2 w-72 rounded-(--radius-lg) border border-(--color-border) bg-(--color-background) p-3 shadow-(--shadow-md)">
					<div className="mb-2 flex items-center justify-between text-sm">
						<span className="font-[500] text-(--color-foreground)">{title}</span>
						<span className="text-(--color-muted-foreground)">{percent}{completedLabel}</span>
					</div>
					<div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-(--color-muted)">
						<div
							className="h-full rounded-full bg-(--color-success) transition-[width]"
							style={{ width: `${percent}%` }}
						/>
					</div>
					<ul className="flex flex-col gap-1.5">
						{items.map((item) => (
							<li key={item.id} className="flex items-center gap-2 text-sm">
								<span
									aria-hidden
									className={cn(
										'flex size-4 items-center justify-center rounded-full border',
										item.done
											? 'border-(--color-success) bg-(--color-success) text-white'
											: 'border-(--color-border)',
									)}
								>
									{item.done && <CheckIcon className="size-3" />}
								</span>
								<span
									className={
										item.done
											? 'text-(--color-muted-foreground) line-through'
											: 'text-(--color-foreground)'
									}
								>
									{item.label}
								</span>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
